import {
	Component,
	ComponentRef,
	Input,
	ViewChild,
	ElementRef,
	OnDestroy,
	ApplicationRef,
	createComponent,
	EnvironmentInjector,
	Injector
} from '@angular/core';
import { QohPopoverPanelComponent } from './qoh-popover-panel.component';
import { QohPopoverDocumentService } from './qoh-popover-document.service';

let qohPopoverUid = 0;

@Component({
	selector: 'app-qoh-popover',
	template: `
		<button
			#trigger
			type="button"
			class="qoh-popover-trigger"
			[attr.id]="triggerId"
			[attr.aria-expanded]="panelOpen"
			[attr.aria-controls]="panelOpen ? panelId : null"
			aria-haspopup="true"
			(mouseenter)="onTriggerMouseEnter()"
			(mouseleave)="onTriggerMouseLeave()"
			(click)="onTriggerClick($event)"
		>
			<ng-content></ng-content>
		</button>
	`,
	styles: [`
		.qoh-popover-trigger {
			display: inline-block;
			border: none;
			background: transparent;
			padding: 0;
			margin: 0;
			font: inherit;
			color: inherit;
			text-align: inherit;
			cursor: pointer;
			-webkit-tap-highlight-color: transparent;
		}
		.qoh-popover-trigger:focus-visible {
			outline: 2px solid #1565c0;
			outline-offset: 2px;
			border-radius: 4px;
		}
	`]
})
export class QohPopoverComponent implements OnDestroy {
	@Input() item: { qoh?: any[]; canviewqoh?: number; locqoh?: number } | null = null;
	@ViewChild('trigger') triggerRef: ElementRef<HTMLElement>;

	readonly triggerId = `qoh-popover-tr-${++qohPopoverUid}`;
	readonly panelId = `qoh-popover-${qohPopoverUid}`;

	panelOpen = false;

	private panelRef: ComponentRef<QohPopoverPanelComponent> | null = null;
	private panelHostEl: HTMLElement | null = null;
	private leaveTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly boundPanelEnter = () => this.cancelCloseTimer();
	private readonly boundPanelLeave = () => this.scheduleClose();

	private readonly docHandlers = {
		onDocumentKeydown: (ev: KeyboardEvent) => {
			if (ev.key === 'Escape' && this.panelRef) {
				ev.stopPropagation();
				this.destroyPanel();
			}
		},
		onDocumentClick: (ev: MouseEvent) => {
			if (!this.panelRef) return;
			const t = ev.target as Node;
			if (this.triggerRef?.nativeElement.contains(t)) return;
			if (this.panelHostEl?.contains(t)) return;
			this.destroyPanel();
		}
	};

	constructor(
		private appRef: ApplicationRef,
		private injector: Injector,
		private envInjector: EnvironmentInjector,
		private qohPopoverDocument: QohPopoverDocumentService
	) {}

	onTriggerMouseEnter() {
		this.cancelCloseTimer();
		if (!this.panelRef) {
			this.openPanel();
		}
	}

	onTriggerMouseLeave() {
		this.scheduleClose();
	}

	onTriggerClick(ev: MouseEvent) {
		ev.stopPropagation();
		this.cancelCloseTimer();
		if (this.panelRef) {
			this.destroyPanel();
		} else {
			this.openPanel();
		}
	}

	private scheduleClose() {
		this.cancelCloseTimer();
		this.leaveTimer = setTimeout(() => this.destroyPanel(), 220);
	}

	private cancelCloseTimer() {
		if (this.leaveTimer) {
			clearTimeout(this.leaveTimer);
			this.leaveTimer = null;
		}
	}

	private openPanel() {
		if (this.panelRef) return;
		const ref = createComponent(QohPopoverPanelComponent, {
			environmentInjector: this.envInjector,
			elementInjector: this.injector
		});
		ref.instance.item = this.item;
		ref.instance.regionId = this.panelId;
		ref.instance.labelledById = `${this.panelId}-heading`;
		this.appRef.attachView(ref.hostView);
		this.panelHostEl = ref.location.nativeElement as HTMLElement;
		document.body.appendChild(this.panelHostEl);
		this.panelHostEl.addEventListener('mouseenter', this.boundPanelEnter);
		this.panelHostEl.addEventListener('mouseleave', this.boundPanelLeave);
		this.panelRef = ref;
		this.panelOpen = true;
		this.qohPopoverDocument.attach(this.docHandlers);

		requestAnimationFrame(() => {
			const el = this.triggerRef?.nativeElement;
			if (el && this.panelRef) {
				const rect = el.getBoundingClientRect();
				this.panelRef.instance.updatePosition(rect);
				this.panelRef.changeDetectorRef.detectChanges();
				requestAnimationFrame(() => {
					if (this.panelRef) {
						this.panelRef.instance.clampToViewport();
						this.panelRef.changeDetectorRef.detectChanges();
					}
				});
			}
		});
	}

	private destroyPanel() {
		this.cancelCloseTimer();
		this.qohPopoverDocument.detach(this.docHandlers);
		if (this.panelHostEl) {
			this.panelHostEl.removeEventListener('mouseenter', this.boundPanelEnter);
			this.panelHostEl.removeEventListener('mouseleave', this.boundPanelLeave);
			this.panelHostEl = null;
		}
		if (this.panelRef) {
			this.appRef.detachView(this.panelRef.hostView);
			this.panelRef.destroy();
			this.panelRef = null;
		}
		this.panelOpen = false;
	}

	ngOnDestroy() {
		this.destroyPanel();
	}
}
